/* tslint:disable:max-line-length */
import { FuseNavigationItem } from '@fuse/components/navigation';

type SectionType = 'group' | 'collapsable';

const basic = (id: string, title: string, icon: string, link: string, activeOn?: string[]): FuseNavigationItem =>
    ({ id, title, type: 'basic', icon, link, ...(activeOn ? { activeOn } : {}) });

const ADMIN_CLIENT_DETAIL = ['/admin/clients/detail'];

const node = (
    type: SectionType,
    id: string,
    title: string,
    icon: string,
    children: FuseNavigationItem[],
    link?: string
): FuseNavigationItem => ({ id, title, type, icon, ...(link ? { link } : {}), children });

const homeItem: FuseNavigationItem = basic('home', 'Home', 'mat_outline:home', '/home');

export const defaultNavigation: FuseNavigationItem[] = [homeItem];

export const settingMenuItems: FuseNavigationItem[] = [
    basic('settings-options', 'Options', 'mat_outline:dynamic_form', '/admin/settings/options'),
    basic('settings-distribution', 'Distribution', 'mat_outline:forward_to_inbox', '/admin/settings/distribution'),
    basic('settings-standards', 'Standards', 'mat_outline:integration_instructions', '/admin/settings/standards'),
    basic('settings-industry-code', 'Industry Codes', 'mat_outline:checklist', '/admin/settings/industry-code'),
    basic('settings-industry-competency', 'Industry Competencies', 'mat_outline:checklist', '/admin/settings/industry-competency'),
    basic('settings-industry-codes-to-standard', 'Industry Codes to Standards', 'mat_outline:checklist', '/admin/settings/industry-codes-to-standard'),
    basic('settings-test-manager', 'Test Management', 'mat_outline:folder_open', '/admin/settings/test-manager'),
    basic('settings-legal', 'Legal', 'mat_outline:gavel', '/admin/settings/legal'),
    basic('settings-geo', 'Geography', 'mat_outline:map', '/admin/settings/geo'),
    basic('settings-schema-manager', 'Schema Manager', 'mat_outline:settings_input_component', '/admin/settings/schema'),
    basic('settings-quote-profiles', 'Sales Quote Profiles', 'mat_outline:manage_accounts', '/admin/settings/quote-profiles'),
    basic('settings-standard-profiles', 'Standard Profiles', 'mat_outline:manage_accounts', '/admin/settings/standard-profiles'),
    basic('settings-expense-report-options', 'Expense Report Options', 'mat_outline:manage_accounts', '/admin/settings/expense-report-options'),
    basic('settings-email-template', 'Email Template', 'mat_outline:email', '/admin/settings/email-template'),
];

const adminClientsChildren: FuseNavigationItem[] = [
    basic('clients-list', 'Clients', 'mat_outline:view_list', '/admin/clients/list', ADMIN_CLIENT_DETAIL),
    basic('clients-report', 'Reports', 'mat_outline:view_list', '/admin/clients/client-reports'),
    basic('clients-add', 'Add New Client', 'mat_outline:add_circle_outline', '/admin/clients/add'),
    basic('clients-add-old', 'Add New Client Old Form', 'mat_outline:add_circle_outline', '/admin/clients/add-old'),
];

const adminSalesChildren: FuseNavigationItem[] = [
    basic('sales-quote-requests', 'Applications', 'mat_outline:send_and_archive', '/admin/sales/quote-requests'),
    basic('sales-quotes', 'Quotes', 'mat_outline:request_quote', '/admin/sales/quotes'),
];

const adminAuditsChildren: FuseNavigationItem[] = [
    basic('audits-list', 'Audits', 'mat_outline:view_list', '/admin/audits/list', ['/admin/audits/details']),
    basic('audits-calendar', 'Audit Calendar', 'mat_outline:calendar_today', '/admin/audits/calendar'),
    basic('audits-certificates', 'Audit Certificates', 'heroicons_outline:office-building', '/admin/audits/certificates'),
    basic('risk-matrix', 'Risk Matrix', 'heroicons_outline:shield-exclamation', '/admin/audits/risk-matrix'),
    basic('ai-analysis', 'AI Agent Analysis', 'heroicons_outline:chip', '/admin/audits/ai-analysis'),
];

const adminBillingChildren: FuseNavigationItem[] = [
    basic('expenses-list', 'Expenses', 'mat_outline:view_list', '/admin/billing/expenses/list'),
    basic('expenses-report', 'Reports', 'mat_outline:view_list', '/admin/billing/expenses-report'),
    basic('invoices-list', 'Invoices', 'mat_outline:receipt', '/admin/invoice/list', ['/admin/invoice/view-invoice']),
];

const adminPersonnelChildren: FuseNavigationItem[] = [
    basic('personnel-users', 'Users', 'mat_outline:manage_accounts', '/admin/personnel/users'),
    basic('personnel-auditors', 'Auditors', 'mat_outline:rate_review', '/admin/personnel/auditors'),
    basic('personnel-sales', 'Sales Reps', 'mat_outline:support_agent', '/admin/personnel/sales-rep'),
    basic('personnel-staff', 'Staff/Committees', 'mat_outline:badge', '/admin/personnel/staff'),
    basic('personnel-clients', 'Clients', 'mat_outline:badge', '/admin/personnel/client'),
    basic('personnel-mmr', 'MMR', 'mat_outline:groups', '/admin/personnel/mmr'),
];

const adminDocumentsChildren: FuseNavigationItem[] = [
    basic('documents-control', 'Document Control', 'mat_outline:folder_special', '/admin/documents'),
    basic('audit-document-templates', 'Audit Document Templates', 'mat_outline:folder_special', '/admin/document-templates'),
    basic('documents-files', 'File Manager', 'mat_outline:folder_shared', '/admin/file-manager'),
];

const adminEmailManagerSection: FuseNavigationItem = node(
    'collapsable', 'email-manager', 'Email Manager', 'mat_outline:email',
    [basic('email-manager-email-history', 'Email History', 'mat_outline:email', '/admin/email-manager/email-history')],
    '/admin/email-manager'
);

const adminActivityItem: FuseNavigationItem = basic('Activity', 'Activity Console', 'mat_outline:sync_alt', '/admin/activity');

const buildAdminSections = (sectionType: SectionType): FuseNavigationItem[] => [
    node(sectionType, 'clients', 'Clients', 'mat_outline:business', adminClientsChildren),
    node(sectionType, 'sales', 'Sales', 'mat_outline:attach_money', adminSalesChildren),
    node(sectionType, 'audits', 'Audits', 'mat_outline:travel_explore', adminAuditsChildren),
    node(sectionType, 'billing', 'Billing', 'heroicons_outline:currency-dollar', adminBillingChildren),
    node(sectionType, 'personnel', 'Personnel', 'mat_outline:badge', adminPersonnelChildren),
    node(sectionType, 'documents', 'Documents', 'mat_outline:snippet_folder', adminDocumentsChildren),
    node(sectionType, 'settings', 'Settings', 'mat_outline:settings', settingMenuItems, '/admin/settings'),
    adminEmailManagerSection,
    adminActivityItem,
];

const salesRepClientsItem: FuseNavigationItem = basic('clients', 'Clients', 'mat_outline:business', '/sales-rep/clients/list', ['/sales-rep/clients/detail']);

const salesRepSalesChildren: FuseNavigationItem[] = [
    basic('applications', 'Applications', 'mat_outline:send_and_archive', '/sales-rep/sales/quote-requests'),
    basic('sales-quotes', 'Quotes', 'mat_outline:request_quote', '/sales-rep/sales/quotes'),
];

const buildSalesRepSalesSection = (sectionType: SectionType): FuseNavigationItem =>
    node(sectionType, 'sales', 'Sales', 'mat_outline:attach_money', salesRepSalesChildren);

const salesRepBillingChildren: FuseNavigationItem[] = [
    basic('invoices-list', 'Invoices', 'mat_outline:receipt', '/sales-rep/invoices'),
];

const buildSalesRepBillingSection = (sectionType: SectionType): FuseNavigationItem =>
    node(sectionType, 'billing', 'Billing', 'heroicons_outline:currency-dollar', salesRepBillingChildren);

export const compactNavigation: FuseNavigationItem[] = [
    homeItem,
    basic('clients', 'Clients', 'heroicons_outline:office-building', '/admin/clients/list', ADMIN_CLIENT_DETAIL),
    basic('audits', 'Audits', 'heroicons_outline:identification', '/admin/audits'),
    basic('billing', 'Billing', 'heroicons_outline:currency-dollar', '/admin/billing'),
    basic('documents', 'Document Manager', 'heroicons_outline:document', '/admin/documents'),
    basic('files', 'File Manager', 'heroicons_outline:document-search', '/admin/file-manager'),
    basic('reports', 'Reports', 'heroicons_outline:presentation-chart-line', '/admin/reports'),
    basic('quote-requests', 'Applications', 'mat_outline:request_quote', '/admin/quotes'),
];

export const futuristicNavigation: FuseNavigationItem[] = [
    basic('example', 'Example', 'heroicons_outline:chart-pie', '/example'),
];

export const horizontalNavigation: FuseNavigationItem[] = [
    homeItem,
    node('group', 'clients', 'Clients', 'heroicons_outline:office-building', [
        basic('clients.search', 'Search', 'heroicons_outline:office-building', '/clients/list'),
        basic('clients.application', 'Applications', 'heroicons_outline:clipboard-list', '/applications'),
    ]),
    basic('audits', 'Audit Scheduling', 'mat_outline:travel_explore', '/admin/audits'),
    basic('billing', 'Billing', 'heroicons_outline:currency-dollar', '/admin/billing'),
    basic('documents', 'Document Manager', 'heroicons_outline:document', '/admin/documents'),
    basic('files', 'File Manager', 'heroicons_outline:document-search', '/admin/file-manager'),
    basic('reports', 'Reports', 'heroicons_outline:presentation-chart-line', '/reports'),
    basic('audits', 'Audit Scheduling', 'mat_outline:travel_explore', '/admin/audits'),
];

export const adminNavigation: FuseNavigationItem[] = [
    homeItem,
    node('group', 'admin', 'Admin', 'mat_outline:admin_panel_settings', buildAdminSections('group')),
];

export const adminCompactNavigation: FuseNavigationItem[] = [
    homeItem,
    ...buildAdminSections('collapsable'),
];

export const auditorNavigation: FuseNavigationItem[] = [
    basic('quote-requests', 'Home', 'mat_outline:home', '/home'),
    basic('my-audits', 'My Audits', 'mat_outline:rate_review', '/auditor/audits', ['/auditor/audit/']),
    basic('my-tests', 'My Tests', 'mat_outline:file_present', '/auditor/tests'),
    basic('my-calendar', 'My Calendar', 'mat_outline:calendar_today', '/auditor/calendar'),
    basic('my-reports', 'My Reports', 'mat_outline:inventory', '/auditor/reports'),
    basic('my-invoices', 'My Invoices', 'mat_outline:rate_review', '/auditor/invoices'),
    basic('my-documents', 'Documents', 'heroicons_outline:document', '/auditor/documents'),
];

export const customerNavigation: FuseNavigationItem[] = [
    basic('quote-requests', 'Home', 'mat_outline:home', '/home'),
    basic('my-quote', 'Quotes', 'mat_outline:request_quote', '/customer/my-quotes'),
    basic('my-applications', 'Applications', 'heroicons_outline:clipboard-list', '/customer/applications'),
    basic('my-clients', 'Clients', 'mat_outline:business', '/customer/clients'),
    basic('my-audits', 'Audits', 'mat_outline:travel_explore', '/customer/audits'),
    basic('my-documents', 'Documents', 'mat_outline:snippet_folder', '/customer/documents'),
];

export const salesRepNavigation: FuseNavigationItem[] = [
    basic('quote-requests', 'My Requests', 'mat_outline:request_quote', '/home'),
    node('group', 'sales-rep', 'Sales Rep', 'mat_outline:admin_panel_settings', [
        homeItem,
        salesRepClientsItem,
        buildSalesRepSalesSection('group'),
        buildSalesRepBillingSection('group'),
    ]),
];

export const salesRepCompactNavigation: FuseNavigationItem[] = [
    homeItem,
    salesRepClientsItem,
    buildSalesRepSalesSection('collapsable'),
    buildSalesRepBillingSection('collapsable'),
];

export const salesManagerNavigation: FuseNavigationItem[] = [
    basic('quote-requests', 'Quote Request Queue', 'mat_outline:request_quote', '/home'),
];

export const clientNavigation: FuseNavigationItem[] = [
    basic('dashboard', 'Dashboard', 'mat_outline:home', '/home'),
    basic('client-matrix', 'Client Matrix', 'heroicons_outline:shield-check', '/client/client-matrix'),
    basic('basic-Details', 'Basic Details', 'heroicons_outline:user-circle', '/client/basic-details'),
    basic('certificate-Standards', 'Certificate Standards', 'heroicons_outline:check-circle', '/client/certificate-standards'),
    basic('sites', 'Sites', 'heroicons_outline:location-marker', '/client/sites'),
    basic('application', 'Applications', 'heroicons_outline:user-add', '/client/application'),
    basic('quotes', 'Quotes', 'heroicons_outline:currency-dollar', '/client/quotes'),
    basic('certificate-management', 'Certificate Management', 'heroicons_outline:identification', '/client/certificate-management'),
    basic('audits', 'Audits', 'heroicons_outline:search-circle', '/client/audits'),
    basic('risk-matrix', 'Risk Matrix', 'heroicons_outline:shield-exclamation', '/client/risk-matrix'),
    basic('contact', 'Contact', 'heroicons_outline:user-group', '/client/contact'),
    basic('attached-documents', 'Attach Document', 'heroicons_outline:document-duplicate', '/client/attached-documents'),
    basic('activity-invoice', 'Active Invoice', 'mat_outline:receipt', '/client/activity-invoice'),
    basic('activity-console', 'Activity Console', 'mat_outline:sync_alt', '/client/activity-console'),
];

export const mmrNavigation: FuseNavigationItem[] = [
    basic('dashboard', 'Dashboard', 'mat_outline:home', '/home'),
    basic('basic-Details', 'Basic Details', 'heroicons_outline:user-circle', '/mmr/basic-details'),
    basic('clients', 'Clients', 'mat_outline:business', '/mmr/clients/list', ['/mmr/client/details']),
    basic('attached-documents', 'Attach Document', 'heroicons_outline:document-duplicate', '/mmr/attached-documents'),
    basic('activity-console', 'Activity Console', 'mat_outline:sync_alt', '/mmr/activity'),
];
